import React, { useEffect, useState } from 'react';
import { useAuthAxios } from '../authfetch';
import { apiurl } from '../../common/constants';
import { useUser } from '@clerk/clerk-react';

export default function Roles() {
    const authAxios = useAuthAxios();
    const { user } = useUser();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Search Handling
    const [searchEmail, setSearchEmail] = useState("");
    const [searchedUser, setSearchedUser] = useState(null);
    const [searchError, setSearchError] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [actionMessage, setActionMessage] = useState(null);

    const isSuperAdmin = user?.publicMetadata?.role === 'superadmin';

    const fetchAdmins = async () => {
        try {
            const response = await authAxios.get(`${apiurl}/roles/admins`);
            if (response.data && response.data.success) {
                setAdmins(response.data.data);
            } else {
                setError("Failed to fetch admins");
            }
        } catch (err) {
            console.error("Error fetching admins:", err);
            setError("An error occurred while fetching admins");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, [authAxios]);

    const handleSearch = async (e) => {
        e.preventDefault();
        setSearchedUser(null);
        setSearchError(null);
        setActionMessage(null);
        setSearchLoading(true);

        try {
            const response = await authAxios.get(`${apiurl}/roles/search?email=${searchEmail}`);
            if (response.data && response.data.success) {
                setSearchedUser(response.data.data);
            } else {
                setSearchError("User not found");
            }
        } catch (err) {
            console.error("Search error:", err);
            setSearchError(err.response?.data?.message || "User not found or error occurred");
        } finally {
            setSearchLoading(false);
        }
    };

    const handleRoleUpdate = async (userId, newRole) => {
        if (!window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) return;
        
        try {
            const response = await authAxios.put(`${apiurl}/roles/update-role`, { userId, newRole });
            
            if (response.data && response.data.success) {
                setActionMessage(`Successfully updated role to ${newRole}`);
                // Refresh data
                setSearchedUser({ ...searchedUser, role: newRole });
                fetchAdmins(); 
            }
        } catch (err) {
            console.error("Update error:", err);
            setActionMessage("Failed to update user role");
        }
    };

    if (loading) return <div className="p-3">Loading...</div>;
    if (error) return <div className="p-3 text-danger">{error}</div>;

    return (
      <div className="p-3">
        {/* Search Section */}
        <div className="mb-4 p-3 bg-white border rounded shadow-sm">
            <h4>Search User to Change Role</h4>
            <form onSubmit={handleSearch} className="d-flex gap-2 mb-3">
                <input 
                    type="email" 
                    className="form-control" 
                    placeholder="Enter user email" 
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    required
                />
                <button type="submit" className="btn btn-primary" disabled={searchLoading}>
                    {searchLoading ? 'Searching...' : 'Search'}
                </button>
            </form>

            {searchError && <div className="alert alert-warning">{searchError}</div>}
            {actionMessage && <div className="alert alert-info">{actionMessage}</div>}

            {searchedUser && (
                <div className="card">
                    <div className="card-body">
                        <h5 className="card-title">{searchedUser.name}</h5>
                        <p className="card-text">Email: {searchedUser.email}</p>
                        <p className="card-text">Current Role: <span className="badge bg-secondary">{searchedUser.role}</span></p>

                        {isSuperAdmin ? (
                             <div className="mt-3">
                                {(searchedUser.role === 'user' || searchedUser.role === 'customer') ? (
                                    <button 
                                        className="btn btn-success"
                                        onClick={() => handleRoleUpdate(searchedUser._id, 'admin')}
                                    >
                                        Make Admin
                                    </button>
                                ) : searchedUser.role === 'admin' ? (
                                    <button 
                                        className="btn btn-warning"
                                        onClick={() => handleRoleUpdate(searchedUser._id, 'customer')}
                                    >
                                        Make Customer
                                    </button>
                                ) : (
                                    <span className="text-muted">
                                        {searchedUser.role === 'superadmin' ? 'Cannot change superadmin role' : 'Role cannot be changed'}
                                    </span>
                                )}
                             </div>
                        ) : (
                            <p className="text-muted mt-2"><small>Only Superadmins can change roles.</small></p>
                        )}
                    </div>
                </div>
            )}
        </div>

        <hr />

        <h3>Admins List</h3>
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {admins.length > 0 ? (
                admins.map((admin) => (
                  <tr key={admin._id}>
                    <td>{admin.name}</td>
                    <td>{admin.email}</td>
                    <td>
                      <span className={`badge ${admin.role === 'superadmin' ? 'bg-danger' : 'bg-primary'}`}>
                        {admin.role}
                      </span>
                    </td>
                    <td>{new Date(admin.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">No admins found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
}
